import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

// Per-user appearance: everyone picks their own brand theme + light/dark mode.
// A choice is personal — it is saved to the user's profile (server) and cached
// in localStorage, and never forced on other users. `cyber` is the default
// (the original look); the instance may seed a different default via the
// data-brand-theme attribute set before React mounts (customer instances do).
export const BRANDS = ["cyber", "clean", "bold", "terminal"];
export const BRAND_LABELS = { cyber: "Cyber", clean: "Clean", bold: "Bold", terminal: "Terminal" };
const MODES = ["dark", "light"];
const API_URL = process.env.REACT_APP_API_URL || "";

const ThemeContext = createContext({
  theme: "dark", mode: "dark", brand: "cyber",
  toggleTheme: () => {}, setMode: () => {}, setBrand: () => {},
});

function readInitial(key, fallbackAttr, allowed, def) {
  try {
    const ls = localStorage.getItem(key);
    if (ls && allowed.includes(ls)) return ls;
  } catch { /* ignore */ }
  const attr = typeof document !== "undefined" ? document.documentElement.getAttribute(fallbackAttr) : null;
  if (attr && allowed.includes(attr)) return attr;
  return def;
}

export function ThemeProvider({ children }) {
  const { agent, updateAgent } = useAuth();
  // localStorage → instance default (DOM attr) → hard default. Instant, no flash.
  const [mode,  setModeState]  = useState(() => readInitial("midtc-theme", "data-theme",       MODES,  "dark"));
  const [brand, setBrandState] = useState(() => readInitial("midtc-brand", "data-brand-theme", BRANDS, "cyber"));

  // Apply + persist locally whenever either changes.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    try { localStorage.setItem("midtc-theme", mode); } catch { /* ignore */ }
  }, [mode]);
  useEffect(() => {
    document.documentElement.setAttribute("data-brand-theme", brand);
    try { localStorage.setItem("midtc-brand", brand); } catch { /* ignore */ }
  }, [brand]);

  // When the signed-in user's profile loads, adopt THEIR saved preference
  // (so it follows them across devices/browsers). Local choice drives boot.
  useEffect(() => {
    if (!agent) return;
    if (agent.ui_theme && BRANDS.includes(agent.ui_theme)) setBrandState(agent.ui_theme);
    if (agent.ui_mode  && MODES.includes(agent.ui_mode))   setModeState(agent.ui_mode);
  }, [agent?.ui_theme, agent?.ui_mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort save to the user's profile — only affects this user's row.
  const persist = useCallback((patch) => {
    let token = null;
    try { token = localStorage.getItem("midtc_token"); } catch { /* ignore */ }
    if (!token) return;
    fetch(`${API_URL}/api/agents/me`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((a) => { if (a && updateAgent) updateAgent({ ui_theme: a.ui_theme, ui_mode: a.ui_mode }); })
      .catch(() => { /* offline is fine — localStorage still holds it */ });
  }, [updateAgent]);

  const setMode  = useCallback((m) => { if (MODES.includes(m))  { setModeState(m);  persist({ ui_mode: m }); } }, [persist]);
  const setBrand = useCallback((b) => { if (BRANDS.includes(b)) { setBrandState(b); persist({ ui_theme: b }); } }, [persist]);
  const toggleTheme = useCallback(() => setMode(mode === "dark" ? "light" : "dark"), [mode, setMode]);

  return (
    <ThemeContext.Provider value={{ theme: mode, mode, brand, toggleTheme, setMode, setBrand, BRANDS, BRAND_LABELS }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
